import React, { useState, useEffect } from 'react';
import { FaRegWindowClose } from "react-icons/fa";
import "./Filters.css";
import FilterSetup from "./FilterSetup";
import { useDataContext } from "../DataContext/PortfolioDataContext";
import Dropdown from "../DropDowns/DropDown";


const FilterOption = ({
  options,
  selectedValue,
  onChange,
  name,
  type = "radio",
  disabledOptions = [],
}: any) => {
  const uniqueOptions = options.filter(
    (option: string, index: number, self: string[]) =>
      option.toLowerCase() !== "unknown" &&
      self.findIndex((opt: string) => opt.toLowerCase() === option.toLowerCase()) === index
  );


  const visibleOptions = uniqueOptions.filter(
    (option: string) => !disabledOptions.includes(option)
  );

  return (
    <div className="mb-4">
      <div
        className={`grid ${name === "assetType" ? "grid-cols-2 gap-x-8" : "grid-cols-3"} gap-4`}
      >
        <label
          key="All"
          className={`flex items-center cursor-pointer transition-all duration-300 ease-in-out ${
            selectedValue === "" ? "text-red-600" : "text-gray-600"
          }`}
        >
          <input
            type={type}
            name={name}
            value=""
            checked={selectedValue === ""}
            onChange={() => onChange("")}
            className="mr-2 accent-red-600"
          />
          All
        </label>

        {/* Only render non-disabled options */}
        {visibleOptions.map((option: string) => (
          <label
            key={option}
            className={`flex items-center cursor-pointer transition-all duration-300 ease-in-out ${
              selectedValue === option ? "text-red-600" : "text-gray-600"
            }`}
          >
            <input
              type={type}
              name={name}
              value={option}
              checked={selectedValue === option}
              onChange={() => onChange(option)}
              className="mr-2 accent-red-600"
            />
            {option}
          </label>
        ))}
      </div>
    </div>
  );
};
 
interface FiltersProps {
  setIsFiltersOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onApplyFilter: (filters: any) => void;

}

const Filters: React.FC<FiltersProps> = ({ setIsFiltersOpen, onApplyFilter }) => {
  const { filters, setFilters } = useDataContext();
  const [selectedAssetType, setSelectedAssetType] = useState(filters.selectedAssetType);
  const [selectedISO, setSelectedISO] = useState(filters.selectedISO);
  const [sortField, setSortField] = useState(filters.sortField);
  const [sortOrder, setSortOrder] = useState(filters.sortOrder);
  const [groupByField, setGroupByField] = useState(filters.groupByField);

  const [assetTypes, setAssetTypes] = useState<string[]>([]);
  const [isos, setIsos] = useState<string[]>([]);
  const [disabledAssetTypes, setDisabledAssetTypes] = useState<string[]>([]);
  const [disabledIsos, setDisabledIsos] = useState<string[]>([]);
  const { assetData } = useDataContext();



  useEffect(() => {
    if (assetData) {
      const availableAssetTypes = assetData.map((item: any) => item.asset_type);
      const availableIsos = assetData.map((item: any) => item.ISO);

      setDisabledAssetTypes(assetTypes.filter(type => !availableAssetTypes.includes(type)));
      setDisabledIsos(isos.filter(iso => !availableIsos.includes(iso)));

    }
  }, [assetData, assetTypes, isos]);

  useEffect(() => {
    setSelectedAssetType(filters.selectedAssetType);
    setSelectedISO(filters.selectedISO);
    setSortField(filters.sortField);
    setSortOrder(filters.sortOrder);
    setGroupByField(filters.groupByField);
  }, [filters]);


  const handleApply = () => {
    const updatedFilters = {
      selectedAssetType: selectedAssetType || "", 
      selectedISO: selectedISO || "", 
      sortField,
      sortOrder,
      groupByField,
    };

    setFilters(updatedFilters);
    localStorage.setItem("filters", JSON.stringify(updatedFilters)); 

    const filtersData = {
      filters: {
        iso: { value: updatedFilters.selectedISO ? [updatedFilters.selectedISO] : [] },
        asset_type: { value: updatedFilters.selectedAssetType ? [updatedFilters.selectedAssetType] : [] },
      },
      sort_by: sortField ? `${sortField} ${sortOrder}` : null,
      group_by: groupByField || null,
    };

    onApplyFilter(filtersData);
    setIsFiltersOpen(false);
  };

  const sortOptions = [
    { label: "None", value: "" },
    { label: "Asset Type", value: "asset_type" },
    { label: "Asset Name", value: "asset_name" },
    { label: "ISO", value: "ISO" },
    { label: "RT_Volume", value: "RT_VOL" },
    {label:"DA_Volume",value:"DA_VOL"},
    { label: "RT_LMP", value: "RT_LMP" },
    { label: "DA_LMP", value: "DA_LMP" },
  ];

  const groupByOptions = [
    { label: "None", value: "" },
    { label: "Asset Type", value: "asset_type" },
    { label: "Asset Name", value: "asset_name" },
    { label: "ISO", value: "ISO" },
  ];


  return (
    <div className="relative w-full sm:w-64 md:w-72 lg:w-80 xl:w-[360px] bg-white rounded-md shadow-lg h-full overflow-y-auto">
      <button
        className="absolute top-4 right-4 text-gray-600 hover:text-red-500 z-50"
        onClick={() => setIsFiltersOpen(false)}
      >
        <FaRegWindowClose className="w-6 h-6" />
      </button>
      <div className="p-4 bg-[#F7F7FF] rounded-lg">
        <h3 className="text-lg font-bold text-left mb-4">Filters</h3>
        <FilterSetup onAssetTypesFetched={setAssetTypes} onIsosFetched={setIsos} />

        {/* Asset Type Filter */}
        <div className="mb-3 p-4 bg-white rounded-lg shadow-md">
          <h4 className="text-sm font-bold text-gray-700 mb-3">Asset Type</h4>
          <FilterOption
            options={assetTypes}
            selectedValue={selectedAssetType}
            onChange={setSelectedAssetType}
            name="assetType"
            disabledOptions={disabledAssetTypes}
          />
        </div>

        {/* ISO Filter */}
        <div className="mb-3 p-4 bg-white rounded-lg shadow-md">
          <h4 className="text-sm font-bold mb-2">ISO</h4>
          <FilterOption
            options={isos}
            selectedValue={selectedISO}
            onChange={setSelectedISO}
            name="iso"
            disabledOptions={disabledIsos}
          />
        </div>

        {/* Sort By */}
        <div className="mb-3 p-4 bg-white rounded-lg shadow-md">
          <h4 className="text-sm font-bold text-gray-700 mb-3">Sort By</h4>
          <div className="font-poppins">
            <Dropdown
              options={sortOptions}
              selectedValue={sortField}
              onValueChange={setSortField}
              className="w-full p-1 border border-gray-300 rounded-md text-sm"
              placeholder="Select Sort Field"
              disabled={false}
            />
          </div>
          <div className="flex items-center gap-4 mt-3">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="sortOrder"
                value="Ascending"
                checked={sortOrder === "Ascending"}
                onChange={() => setSortOrder("Ascending")}
                className="accent-red-600"
              />
              <span className="text-sm">Asc</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="sortOrder"
                value="Descending"
                checked={sortOrder === "Descending"}
                onChange={() => setSortOrder("Descending")}
                className="accent-red-600"
              />
              <span className="text-sm">Dsc</span>
            </label>
          </div>
        </div>

        {/* Group By Filter - Use Dropdown */}
        <div className="mb-3 p-4 bg-white rounded-lg shadow-md">
          <h4 className="text-sm font-bold mb-2">Group By</h4>
          <div className="font-poppins">
            <Dropdown
              options={groupByOptions}
              selectedValue={groupByField}
              onValueChange={setGroupByField}
              className="w-full p-1 border border-gray-300 rounded-md text-sm"
              placeholder="Select Group Field"
              disabled={false}
            />
          </div>
        </div>
        {/* Apply Button */}
        <div className="flex justify-center">
          <button
            onClick={handleApply}
            className="w-full py-2 mb-3 bg-[#2C3192] text-white font-semibold rounded-lg hover:bg-[#2C3192] transition duration-100 transform-none"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default Filters;
